import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Empty, Form, Grid, Image, Input, List, Modal, Popconfirm, Select, Space, Spin, Tag, Typography, Upload, message } from "antd";
import { CameraOutlined, CheckCircleOutlined, ClockCircleOutlined, DeleteOutlined, PlusOutlined, SendOutlined, ToolOutlined } from "@ant-design/icons";
import {
    createWorkOrder,
    deleteWorkOrder,
    getMyRooms,
    getWorkOrder,
    getWorkOrders,
    markWorkOrderRead,
    replyWorkOrder,
    uploadWorkOrderImages,
    updateWorkOrderStatus
} from "../services/api";
import { PageLoadingOverlay } from "../components/RoomLoadingScene";

const { Text, Title, Paragraph } = Typography;
const typeOptions = [
    { value: "repair", label: "报修" },
    { value: "cleaning", label: "保洁" },
    { value: "other", label: "其他&建议" }
];
const statusMap = {
    pending: { text: "待处理", color: "red" },
    processing: { text: "处理中", color: "blue" },
    resolved: { text: "已完成", color: "green" },
    closed: { text: "已关闭", color: "default" }
};
const accountRoleMap = {
    landlord: "房东主账号",
    sub_account: "子账号",
    admin: "管理员"
};

const formatTime = value => value ? new Date(value).toLocaleString("zh-CN", { hour12: false }) : "";
const workOrderCache = new Map();

const WorkOrderImage = ({ workOrderId, image }) => {
    const [src, setSrc] = useState("");
    useEffect(() => {
        let active = true;
        let objectUrl = "";
        fetch(`/api/work-orders/${workOrderId}/images/${image.id}/file`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        }).then(response => {
            if (!response.ok) throw new Error("图片读取失败");
            return response.blob();
        }).then(blob => {
            objectUrl = URL.createObjectURL(blob);
            if (active) setSrc(objectUrl);
        }).catch(() => { if (active) setSrc(""); });
        return () => {
            active = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [workOrderId, image.id]);
    return src
        ? <Image src={src} alt={image.file_name || "工单图片"} width={92} height={92} style={{ objectFit: "cover", borderRadius: 8 }} />
        : <div className="work-order-image-loading">图片加载中</div>;
};

const WorkOrderScreen = ({ user }) => {
    const cacheKey = user?.id || "anonymous";
    const cachedData = workOrderCache.get(cacheKey);
    const [items, setItems] = useState(cachedData?.items || []);
    const [rooms, setRooms] = useState(cachedData?.rooms || []);
    const [loading, setLoading] = useState(!cachedData);
    const firstLoad = useRef(true);
    const creatingWorkOrderRef = useRef(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [current, setCurrent] = useState(null);
    const [saving, setSaving] = useState(false);
    const [reply, setReply] = useState("");
    const [fileList, setFileList] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("all");
    const [roomKeyword, setRoomKeyword] = useState("");
    const [roomFilter, setRoomFilter] = useState("");
    const [roomSummary, setRoomSummary] = useState(cachedData?.summary || null);
    const [form] = Form.useForm();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const isTenant = user?.role === "user" || user?.role === "agent";
    const canManage = user?.role === "landlord" || user?.role === "sub_account";

    const loadItems = useCallback(async (quiet = false) => {
        try {
            if (!quiet) setLoading(true);
            const params = {};
            if (statusFilter !== "all") params.status = statusFilter;
            if (typeFilter !== "all") params.type = typeFilter;
            if (canManage && roomFilter) params.room_number = roomFilter;
            const response = await getWorkOrders(params);
            setItems(response.data || []);
            setRoomSummary(response.summary || null);
            if (statusFilter === "all" && typeFilter === "all" && !roomFilter) {
                workOrderCache.set(cacheKey, { ...(workOrderCache.get(cacheKey) || {}), items: response.data || [], summary: response.summary || null });
            }
        } catch (error) {
            if (!quiet) message.error(error?.error || "工单加载失败");
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [statusFilter, typeFilter, canManage, roomFilter, cacheKey]);

    useEffect(() => {
        const quiet = firstLoad.current && Boolean(cachedData);
        firstLoad.current = false;
        loadItems(quiet);
        const timer = setInterval(() => loadItems(true), 15000);
        return () => clearInterval(timer);
    }, [loadItems]);

    useEffect(() => {
        if (!isTenant) return;
        getMyRooms().then(data => {
            const nextRooms = Array.isArray(data) ? data : [];
            setRooms(nextRooms);
            workOrderCache.set(cacheKey, { ...(workOrderCache.get(cacheKey) || {}), rooms: nextRooms });
        }).catch(() => setRooms([]));
    }, [isTenant, cacheKey]);

    const unreadForMe = useCallback(item => item.tenant_id === user?.id ? item.tenant_unread : item.landlord_unread, [user?.id]);
    const roomOptions = useMemo(() => rooms.map(room => ({
        value: room.id,
        label: `${room.building_name || "楼栋"} · ${room.room_number || "房间"}`
    })), [rooms]);

    const openDetail = async item => {
        try {
            const response = await getWorkOrder(item.id);
            setCurrent(response.data);
            setDetailOpen(true);
            if (unreadForMe(item)) {
                await markWorkOrderRead(item.id);
                window.dispatchEvent(new Event("work-order-unread-refresh"));
                loadItems(true);
            }
        } catch (error) {
            message.error(error?.error || "工单详情加载失败");
        }
    };

    const submitCreate = async () => {
        if (creatingWorkOrderRef.current) return;
        creatingWorkOrderRef.current = true;
        setSaving(true);
        try {
            const values = await form.validateFields();
            const created = await createWorkOrder(values);
            let imageUploadFailed = false;
            if (fileList.length) {
                const formData = new FormData();
                fileList.forEach(file => formData.append("files", file.originFileObj || file));
                try {
                    await uploadWorkOrderImages(created.data.id, formData);
                } catch (error) {
                    imageUploadFailed = true;
                    message.warning(error?.error || "工单已提交，但图片上传失败");
                }
            }
            if (!imageUploadFailed) message.success(fileList.length ? `工单已提交，并上传${fileList.length}张图片` : "工单已提交，房东会收到提醒");
            setCreateOpen(false);
            form.resetFields();
            setFileList([]);
            loadItems();
            window.dispatchEvent(new Event("work-order-unread-refresh"));
        } catch (error) {
            if (!error?.errorFields) message.error(error?.error || "提交工单失败");
        } finally {
            creatingWorkOrderRef.current = false;
            setSaving(false);
        }
    };

    const beforeAddImage = file => {
        const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type);
        if (!allowed) {
            message.error("仅支持JPG、PNG、WEBP或GIF图片");
            return Upload.LIST_IGNORE;
        }
        if (file.size > 10 * 1024 * 1024) {
            message.error("每张图片不能超过10MB");
            return Upload.LIST_IGNORE;
        }
        return false;
    };

    const refreshDetail = async id => {
        const response = await getWorkOrder(id);
        setCurrent(response.data);
        await loadItems(true);
        window.dispatchEvent(new Event("work-order-unread-refresh"));
    };

    const submitReply = async () => {
        if (!reply.trim()) return;
        try {
            setSaving(true);
            await replyWorkOrder(current.id, reply.trim());
            setReply("");
            await refreshDetail(current.id);
            message.success("回复已发送");
        } catch (error) {
            message.error(error?.error || "回复失败");
        } finally {
            setSaving(false);
        }
    };

    const changeStatus = async status => {
        try {
            setSaving(true);
            await updateWorkOrderStatus(current.id, status);
            await refreshDetail(current.id);
            message.success("处理状态已更新");
        } catch (error) {
            message.error(error?.error || "状态更新失败");
        } finally {
            setSaving(false);
        }
    };

    const removeWorkOrder = async id => {
        try {
            setSaving(true);
            await deleteWorkOrder(id);
            if (current?.id === id) {
                setDetailOpen(false);
                setCurrent(null);
                setReply("");
            }
            await loadItems(true);
            window.dispatchEvent(new Event("work-order-unread-refresh"));
            message.success("已从你的列表删除，对方的工单仍会保留");
        } catch (error) {
            message.error(error?.error || "删除工单失败");
        } finally {
            setSaving(false);
        }
    };

    const searchRoom = value => {
        const nextValue = String(value ?? roomKeyword).trim();
        setRoomKeyword(nextValue);
        if (nextValue === roomFilter) loadItems();
        else setRoomFilter(nextValue);
    };

    return (
        <div className="work-order-screen">
            {loading && <PageLoadingOverlay caption="正在加载工单，请稍候…" />}
            <div className="work-order-heading">
                <div>
                    <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>{canManage ? "工单处理" : "我的工单"}</Title>
                    <Text type="secondary">{canManage ? "查看租户需求，及时处理并回复" : "入住后可向房东提交报修、保洁或建议"}</Text>
                </div>
                {isTenant && <Button type="primary" size="large" icon={<PlusOutlined />} disabled={!rooms.length} onClick={() => setCreateOpen(true)}>创建工单</Button>}
            </div>

            {isTenant && !rooms.length && <Card size="small" className="work-order-notice">当前没有在住房间，入住后即可创建工单。</Card>}

            <div className="work-order-filters">
                <Select value={statusFilter} onChange={setStatusFilter} options={[
                    { value: "all", label: "全部状态" },
                    ...Object.entries(statusMap).map(([value, item]) => ({ value, label: item.text }))
                ]} />
                <Select value={typeFilter} onChange={setTypeFilter} options={[{ value: "all", label: "全部类型" }, ...typeOptions]} />
                <Button onClick={() => loadItems()}>刷新</Button>
                {canManage && <Input.Search
                    className="work-order-room-search"
                    value={roomKeyword}
                    onChange={event => setRoomKeyword(event.target.value)}
                    onSearch={searchRoom}
                    allowClear
                    enterButton="检索"
                    placeholder="输入房号，如906"
                />}
            </div>

            {canManage && roomFilter && roomSummary && <Card size="small" className="work-order-room-summary">
                <Space wrap>
                    <Text strong>房号“{roomSummary.keyword}”检索结果</Text>
                    <Tag color="blue">全部 {roomSummary.total} 单</Tag>
                    <Tag color="green">当前住户 {roomSummary.current} 单</Tag>
                    <Tag>往期住户 {roomSummary.history} 单</Tag>
                </Space>
            </Card>}

            <Spin spinning={loading}>
                {items.length === 0 ? <Empty description="暂无工单" /> : (
                    <List
                        className="work-order-list"
                        grid={{ gutter: 12, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
                        dataSource={items}
                        renderItem={item => (
                            <List.Item>
                                <Badge dot={unreadForMe(item)} offset={[-8, 8]} color="#ff4d4f">
                                    <Card hoverable className="work-order-card" onClick={() => openDetail(item)}>
                                        <div className="work-order-card-top">
                                            <Space wrap>
                                                <Tag color={item.type === "repair" ? "orange" : item.type === "cleaning" ? "cyan" : "purple"}>{item.type_label}</Tag>
                                                <Tag color={statusMap[item.status]?.color}>{statusMap[item.status]?.text || item.status}</Tag>
                                                {canManage && <Tag color={item.is_current_tenancy ? "green" : "default"}>{item.is_current_tenancy ? "当前住户" : "往期住户"}</Tag>}
                                            </Space>
                                            <Space size={4}>
                                                <Text type="secondary">#{item.id}</Text>
                                                <span onClick={event => event.stopPropagation()}>
                                                    <Popconfirm title="只从你的列表删除此工单？" description="对方未删除时仍可继续查看，双方的数据和图片不会被误删。" okText="确认删除" cancelText="取消" onConfirm={() => removeWorkOrder(item.id)}>
                                                        <Button type="text" danger size="small" icon={<DeleteOutlined />} aria-label="删除我的工单" />
                                                    </Popconfirm>
                                                </span>
                                            </Space>
                                        </div>
                                        <Title level={5} ellipsis className="work-order-card-title" title={item.title}>{item.title}</Title>
                                        <Paragraph ellipsis={{ rows: 2 }} className="work-order-card-description" title={item.description}>{item.description}</Paragraph>
                                        <div className="work-order-room" title={`${item.building_name || "未知楼栋"} · ${item.room_number || "未知房间"}`}><ToolOutlined /> {item.building_name} · {item.room_number}</div>
                                        {(item.images || []).length > 0 && <div className="work-order-photo-count" title={`${item.images.length}张现场图片`}><CameraOutlined /> {item.images.length}张现场图片</div>}
                                        <div className="work-order-card-bottom">
                                            <Text type="secondary" title={canManage ? `租户：${item.tenant_name}` : `房东：${item.landlord_name}`}>{canManage ? `租户：${item.tenant_name}` : `房东：${item.landlord_name}`}</Text>
                                            <Text type="secondary" title={formatTime(item.updated_at)}>{formatTime(item.updated_at)}</Text>
                                        </div>
                                    </Card>
                                </Badge>
                            </List.Item>
                        )}
                    />
                )}
            </Spin>

            <Modal title="创建入住工单" open={createOpen} width={560} centered destroyOnClose maskClosable={false}
                onCancel={() => { setCreateOpen(false); form.resetFields(); setFileList([]); }}
                footer={<Space><Button onClick={() => setCreateOpen(false)}>取消</Button><Button type="primary" loading={saving} onClick={submitCreate}>提交给房东</Button></Space>}
            >
                <Form form={form} layout="vertical" requiredMark={false} initialValues={{ type: "repair" }}>
                    <Form.Item name="room_id" label="入住的房间" rules={[{ required: true, message: "请选择房间" }]}>
                        <Select size="large" placeholder="请选择需要服务的房间" options={roomOptions} />
                    </Form.Item>
                    <Form.Item name="type" label="工单类型" rules={[{ required: true }]}>
                        <Select size="large" options={typeOptions} />
                    </Form.Item>
                    <Form.Item name="description" label="我想说" rules={[{ required: true, message: "请简单说一下需要处理的事项" }, { max: 50, message: "不能超过50个字" }]}>
                        <Input.TextArea rows={3} maxLength={50} showCount placeholder="例如：卫生间水龙头漏水，晚上方便上门" />
                    </Form.Item>
                    <Form.Item label="现场图片（选填，最多5张）" extra="手机可以直接拍照，也可以从相册选择；每张不超过10MB。">
                        <Upload
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            listType="picture-card"
                            multiple
                            maxCount={5}
                            fileList={fileList}
                            beforeUpload={beforeAddImage}
                            onChange={({ fileList: nextList }) => setFileList(nextList.slice(0, 5))}
                        >
                            {fileList.length < 5 && <div><CameraOutlined /><div style={{ marginTop: 6 }}>拍照/上传</div></div>}
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal className="work-order-detail-modal" title={current ? `工单 #${current.id} · ${current.title}` : "工单详情"}
                open={detailOpen} width={720} centered destroyOnClose footer={null}
                onCancel={() => { setDetailOpen(false); setCurrent(null); setReply(""); }}
            >
                {current && <div className="work-order-detail">
                    <div className="work-order-summary">
                        <Space wrap><Tag>{current.type_label}</Tag><Tag color={statusMap[current.status]?.color}>{statusMap[current.status]?.text}</Tag></Space>
                        <Text type="secondary">{current.building_name} · {current.room_number}</Text>
                        <Paragraph style={{ margin: "12px 0 0", whiteSpace: "pre-wrap" }}>{current.description}</Paragraph>
                        {(current.images || []).length > 0 && <div className="work-order-images">
                            <Image.PreviewGroup>
                                {current.images.map(image => <WorkOrderImage key={image.id} workOrderId={current.id} image={image} />)}
                            </Image.PreviewGroup>
                        </div>}
                        {current.completed_at && <div className="work-order-completion-audit">
                            <CheckCircleOutlined />
                            <div>
                                <Text strong>完成账号：{current.completed_by_name || "未知账号"}</Text>
                                <Text type="secondary">{accountRoleMap[current.completed_by_role] || "处理账号"} · {formatTime(current.completed_at)}</Text>
                            </div>
                        </div>}
                    </div>

                    <div className="work-order-actions">
                        {canManage && current.status !== "closed" && <>
                            {current.status === "pending" && <Button type="primary" icon={<ClockCircleOutlined />} loading={saving} onClick={() => changeStatus("processing")}>开始处理</Button>}
                            {current.status !== "resolved" && <Button icon={<CheckCircleOutlined />} loading={saving} onClick={() => changeStatus("resolved")}>标记已完成</Button>}
                            {current.status === "resolved" && <Button loading={saving} onClick={() => changeStatus("closed")}>关闭工单</Button>}
                        </>}
                    </div>

                    <div className="work-order-replies">
                        <div className={`work-order-message ${current.tenant_id === user?.id ? "mine" : "other"}`}>
                            <Text strong>{current.tenant_name}</Text>
                            <Paragraph>{current.description}</Paragraph>
                            <Text type="secondary">{formatTime(current.created_at)}</Text>
                        </div>
                        {(current.replies || []).map(item => {
                            const mine = item.user_id === user?.id;
                            return <div key={item.id} className={`work-order-message ${mine ? "mine" : "other"}`}>
                                <Text strong>{item.username}</Text>
                                <Paragraph>{item.content}</Paragraph>
                                <Text type="secondary">{formatTime(item.created_at)}</Text>
                            </div>;
                        })}
                    </div>

                    {current.status !== "closed" ? <div className="work-order-reply-box">
                        <Input.TextArea value={reply} onChange={event => setReply(event.target.value)} rows={3} maxLength={500} showCount placeholder="输入处理说明或补充信息" />
                        <Button type="primary" icon={<SendOutlined />} loading={saving} disabled={!reply.trim()} onClick={submitReply}>发送回复</Button>
                    </div> : <div className="work-order-closed-tip">此工单已关闭</div>}
                </div>}
            </Modal>
        </div>
    );
};

export default WorkOrderScreen;
